/*!
 * Cart Drawer — Accessibility layer (focus trap, Escape close, inert siblings,
 * aria-expanded sync on the opener button).
 *
 * This file is intentionally standalone vanilla JS with no build-step
 * dependencies. It hooks into the existing cart-drawer custom element
 * (defined in the compiled bundle) via a MutationObserver watching the
 * aria-expanded attribute, so it never has to find every open/close call site.
 *
 * What it does:
 *  1. When aria-expanded flips to "true"  (drawer opens):
 *     - records previously-focused element
 *     - moves focus to the close button (or first focusable element)
 *     - traps Tab / Shift+Tab inside the drawer
 *     - listens for Escape and closes
 *     - applies `inert` + `aria-hidden` to <header>, <main>, <footer> siblings
 *     - syncs aria-expanded on the [data-open-cart] opener button
 *  2. When aria-expanded flips to "false" (drawer closes):
 *     - removes inert / aria-hidden from siblings
 *     - restores focus to the previously-focused element
 *     - removes the keyboard listeners
 *     - syncs aria-expanded on the opener button
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTORS = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var _previouslyFocused = null;
  var _keydownHandler = null;

  function getFocusableElements(drawer) {
    return Array.prototype.slice.call(drawer.querySelectorAll(FOCUSABLE_SELECTORS));
  }

  function getBackgroundSiblings(drawer) {
    var siblings = [];
    ['header', 'main', 'footer'].forEach(function (tag) {
      var el = document.querySelector(tag);
      // Only act on elements that are siblings of the drawer in the DOM tree,
      // i.e. elements that are NOT ancestors of the drawer itself.
      if (el && !el.contains(drawer)) {
        siblings.push(el);
      }
    });
    return siblings;
  }

  function syncOpenerButton(isOpen) {
    var openers = document.querySelectorAll('[data-open-cart]');
    openers.forEach(function (btn) {
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  function onKeydown(drawer, e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      // Delegate to the custom element's own closeCart() so all existing
      // logic (class toggles, position resets, etc.) runs correctly.
      if (typeof drawer.closeCart === 'function') {
        drawer.closeCart();
      } else {
        drawer.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    if (e.key !== 'Tab' && e.keyCode !== 9) return;

    var focusable = getFocusableElements(drawer);
    if (!focusable.length) {
      e.preventDefault();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (e.shiftKey) {
      // Shift+Tab: if on first element, wrap to last
      if (active === first || !drawer.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // Tab: if on last element, wrap to first
      if (active === last || !drawer.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function trapFocus(drawer) {
    // Remove any previous listener first (defensive)
    if (_keydownHandler) {
      document.removeEventListener('keydown', _keydownHandler);
    }
    _keydownHandler = onKeydown.bind(null, drawer);
    document.addEventListener('keydown', _keydownHandler);
  }

  function releaseFocus() {
    if (_keydownHandler) {
      document.removeEventListener('keydown', _keydownHandler);
      _keydownHandler = null;
    }
  }

  function onDrawerOpen(drawer) {
    // 1. Record the element that was focused before the drawer opened
    _previouslyFocused = document.activeElement;

    // 2. Hide siblings from assistive technology
    getBackgroundSiblings(drawer).forEach(function (el) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    });

    // 3. Move focus inside: prefer the close button, fall back to first focusable
    var closeBtn = drawer.querySelector('[data-close-button]');
    var target = closeBtn || getFocusableElements(drawer)[0];
    if (target) {
      // Use a short rAF so the drawer's CSS transition has begun and the
      // element is visible before we try to focus it.
      requestAnimationFrame(function () {
        target.focus();
      });
    }

    // 4. Trap Tab / listen for Escape
    trapFocus(drawer);

    // 5. Keep the opener button's aria-expanded in sync
    syncOpenerButton(true);
  }

  function onDrawerClose(drawer) {
    // 1. Restore sibling visibility
    getBackgroundSiblings(drawer).forEach(function (el) {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    });

    // 2. Release keyboard trap
    releaseFocus();

    // 3. Return focus to the element that opened the drawer
    if (_previouslyFocused && typeof _previouslyFocused.focus === 'function') {
      _previouslyFocused.focus();
      _previouslyFocused = null;
    }

    // 4. Sync opener button
    syncOpenerButton(false);
  }

  function attachObserver(drawer) {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName !== 'aria-expanded') return;
        var isOpen = drawer.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          onDrawerOpen(drawer);
        } else {
          onDrawerClose(drawer);
        }
      });
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  function init() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    // Wait for the custom element to be defined so connectedCallback
    // has already run before our observer attaches.
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer').then(function () {
        attachObserver(drawer);
      });
    } else {
      attachObserver(drawer);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
